import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorComponentData, HttpErrorHandlerService, LoadingRequestStatus, WindowUtilitiesService } from '@grid-ui/common';
import { Subscription } from 'rxjs';
import { DocumentLibraryService } from '../../../api-services';
import { NavService } from '../../../core/services';
import { ServiceOffering } from '../../models';

@Component({
  selector: 'mc-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: false,
  host: {
    class: 'Home layout-grow',
    '[class.Home--loaded]': 'loadingStatus === RequestStatus.loaded',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class HomeComponent implements OnInit, OnDestroy {
  /**
   * The services offering cards to be displayed according to the user
   * permissions & external tools
   **/
  public error: ErrorComponentData | null = null;
  public loadingStatus: LoadingRequestStatus = LoadingRequestStatus.loading;
  public permittedServiceOfferings: ServiceOffering[] = [];
  public readonly RequestStatus = LoadingRequestStatus;

  public indexCatalogueDownloadURL: string;

  private permissionsSubscription: Subscription;
  private windowSupported = false;

  constructor(
    private readonly windowUtilitiesService: WindowUtilitiesService,
    private readonly navService: NavService,
    private readonly httpErrorHandlerService: HttpErrorHandlerService,
    private readonly documentLibraryService: DocumentLibraryService,
    private readonly router: Router,
  ) {}

  public ngOnInit(): void {
    this.indexCatalogueDownloadURL = this.documentLibraryService.getIndexCatalogueDownloadURL();
    this.windowSupported = this.windowUtilitiesService.isSupported();
    this.windowUtilitiesService.scrollToTop();
    this.setServiceOfferings();
  }

  public ngOnDestroy(): void {
    if (this.permissionsSubscription && !this.permissionsSubscription.closed) {
      this.permissionsSubscription.unsubscribe();
    }
  }

  public reloadApp(): void {
    if (this.windowSupported) {
      this.windowUtilitiesService.reloadApp();
    }
  }

  public navigate(offeringOrLink: ServiceOffering | string): void {
    if (typeof offeringOrLink === 'string') {
      void this.router.navigate([offeringOrLink]);
      return;
    }

    const { open_in_window, link } = offeringOrLink as ServiceOffering;

    if (open_in_window) {
      this.windowUtilitiesService.openNewTab(link as string);
    } else {
      void this.router.navigate([link]);
    }
  }

  public setServiceOfferings(): void {
    this.resetLoadingState();

    this.navService
      .serviceOfferings()
      .then((permissions: ServiceOffering[]) => {
        this.permittedServiceOfferings = permissions;
        this.loadingStatus = LoadingRequestStatus.loaded;
      })
      .catch((err: HttpErrorResponse) => {
        // 403s from WhoAmIService are dealt with by navigating to the login page
        // but there can be a delay that allows this error handle to execute.
        // Here we ensure the loading icon is displayed until the navigation happens,
        // instead of showing an error.
        if (err.status !== 403) {
          this.error = this.httpErrorHandlerService.mapHttpErrorResponseToUIComponent(
            err,
            'Try Again',
            this.windowSupported ? 'Reload App' : null,
            'full',
          );
          this.loadingStatus = LoadingRequestStatus.error;
        }
      });
  }

  private resetLoadingState(): void {
    this.loadingStatus = LoadingRequestStatus.loading;
    this.permittedServiceOfferings = [];
    this.error = null;
  }
}
